/**
 * AuthContext.tsx — the auth seam for the app.
 *
 * Auth-READY, not auth-ENFORCING. Today there's no login flow; the
 * Connectivity wizard's api step populates an opaque bearer token. This context
 * is the single source of truth for that token so every remote adapter
 * (RemoteLibraryAdapter, RemoteDocumentAdapter, ApiAdapter) sends the same
 * `Authorization: Bearer <token>` without each one re-plumbing it.
 *
 * When a real identity provider lands, swap the provider implementation here
 * (call `/login`, store the returned JWT, decode the user) — consumers don't
 * change because they only depend on `useAuth()`.
 *
 * The server side mirrors this with an anonymous-safe `optional_bearer`
 * dependency: it validates the JWT signature when `JWT_SECRET` is set, else
 * accepts any bearer as anonymous. Localhost stays frictionless.
 */
import { createContext, useCallback, useContext, useMemo, type ReactNode } from 'react';

import { getDefaultStorage } from '../storage/LocalStorageBackend';
import { usePersistedState } from '../storage/usePersistedState';

const AUTH_TOKEN_KEY = 'auth.token';
const AUTH_WS_URL_KEY = 'auth.wsUrl';
const AUTH_RELAY_KEY = 'auth.relay';

/** A resolved user identity. `null` when anonymous / not yet logged in. */
export interface AuthUser {
  id: string;
  name?: string;
}

export interface Auth {
  /** The bearer token (JWT once real auth lands; opaque today). `null` = anon. */
  token: string | null;
  /** Decoded user, when known. `undefined` for anonymous. */
  user?: AuthUser;
  /** Whether the session is authenticated (a non-null token). UI gates on this
   *  to decide local-vs-remote adapter construction. */
  isAuthenticated: boolean;
  /** Set/replace the token. Called by the login/connect flows. */
  setToken: (token: string | null) => void;
  /** Clear the token (sign out). */
  signOut: () => void;
  /** The WS URL the user connected through (Phase D relay entry). Null for the
   *  local-source default. When set, the Studio constructs an ApiAdapter
   *  pointed at this URL with the token as the API key. */
  wsUrl: string | null;
  /** Whether the connection is in relay mode (Phase D — external model POSTs
   *  ticks; the backend starts no clock). Only meaningful when wsUrl is set. */
  relay: boolean;
  /** Stash the WS URL + relay flag from the connect flow. */
  setWsConnection: (wsUrl: string | null, relay: boolean) => void;
}

const AuthContext = createContext<Auth | null>(null);

const storage = getDefaultStorage();

export function AuthProvider({ children }: { children: ReactNode }): JSX.Element {
  const [token, setTokenState] = usePersistedState<string | null>(storage, AUTH_TOKEN_KEY, null);
  const [wsUrl, setWsUrlState] = usePersistedState<string | null>(storage, AUTH_WS_URL_KEY, null);
  const [relay, setRelayState] = usePersistedState<boolean>(storage, AUTH_RELAY_KEY, false);

  const setToken = useCallback((t: string | null) => setTokenState(t), [setTokenState]);
  const signOut = useCallback(() => {
    setTokenState(null);
    setWsUrlState(null);
    setRelayState(false);
  }, [setTokenState, setWsUrlState, setRelayState]);
  const setWsConnection = useCallback(
    (nextUrl: string | null, nextRelay: boolean) => {
      setWsUrlState(nextUrl);
      setRelayState(nextRelay);
    },
    [setWsUrlState, setRelayState],
  );

  // Decode a lightweight user id from the JWT payload (no verification — the
  // server verifies; the client just reads `sub` for display). When real auth
  // lands this becomes a proper JWT library + a `/me` call.
  const user = useMemo<AuthUser | undefined>(() => {
    if (!token) return undefined;
    try {
      const parts = token.split('.');
      if (parts.length !== 3) return undefined; // not a JWT
      const payload = JSON.parse(atob(parts[1].replace(/-/g, '+').replace(/_/g, '/')));
      const id = payload.sub ?? payload.user_id ?? payload.uid;
      return id ? { id: String(id), name: payload.name } : undefined;
    } catch {
      return undefined;
    }
  }, [token]);

  const value = useMemo<Auth>(
    () => ({
      token,
      user,
      isAuthenticated: token != null,
      setToken,
      signOut,
      wsUrl,
      relay,
      setWsConnection,
    }),
    [token, user, setToken, signOut, wsUrl, relay, setWsConnection],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): Auth {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within <AuthProvider>.');
  return ctx;
}
